import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from './AppIcon';
import Image from './AppImage';
import Button from './ui/Button';

const FeaturedBrandSection = ({ 
  brandName, 
  brandLogo, 
  brandDescription,
  productName,
  productImage,
  productImageAlt,
  productDescription,
  productSpecs,
  productPrice,
  rating,
  reviewCount,
  reviews,
  colorScheme = 'from-blue-500/10 to-cyan-500/10',
  accentColor = 'text-blue-600',
  bgGradient = 'from-blue-50 via-cyan-50 to-teal-50',
  onAddToCompare
}) => {
  const [isAdded, setIsAdded] = useState(false);

  const handleAddToCompare = () => {
    if (onAddToCompare) {
      const productData = {
        id: `featured-${brandName}-${productName}`,
        type: 'Featured Product',
        brandName,
        name: productName,
        image: productImage,
        imageAlt: productImageAlt,
        price: productPrice,
        rating,
        reviewCount,
        specs: productSpecs,
        reviews
      };
      onAddToCompare(productData);
      setIsAdded(true);
      setTimeout(() => setIsAdded(false), 2000);
    }
  };

  return (
    <section className={`relative bg-gradient-to-br ${bgGradient} overflow-hidden py-8 sm:py-12 lg:py-16 border-b-4 border-primary/20`}>
      {/* Animated Background */}
      <motion.div 
        className="absolute inset-0 opacity-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      </motion.div>
      {/* Floating animated blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full mix-blend-multiply filter blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-0 right-0 w-96 h-96 bg-success/10 rounded-full mix-blend-multiply filter blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Featured Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400 to-orange-400 px-6 py-3 rounded-full shadow-elevation-3"
            animate={{
              boxShadow: [
                '0 10px 30px -5px rgba(251, 191, 36, 0.3)',
                '0 20px 40px -5px rgba(251, 191, 36, 0.5)',
                '0 10px 30px -5px rgba(251, 191, 36, 0.3)'
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Icon name="Award" size={24} className="text-white" />
            <span className="text-base sm:text-lg font-bold text-white">Uitgelicht Merk & Product</span>
            <Icon name="Star" size={24} className="text-white fill-current" />
          </motion.div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 perspective-1000">
          {/* Left: Product Showcase */}
          <motion.div
            initial={{ opacity: 0, x: -50, rotateY: -15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <motion.div
              whileHover={{ 
                scale: 1.02,
                rotateX: 5,
                rotateY: -5,
                z: 50
              }}
              transition={{ duration: 0.4 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] hover:shadow-[0_30px_80px_-15px_rgba(0,0,0,0.4)]"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Brand Header */}
              <div className={`bg-gradient-to-r ${colorScheme} p-6 border-b border-border/50`} style={{ transform: 'translateZ(10px)' }}>
                <div className="flex items-center space-x-4">
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Image
                      src={brandLogo}
                      alt={`${brandName} logo`}
                      className="w-16 h-16 rounded-lg shadow-elevation-2"
                    />
                  </motion.div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-foreground">{brandName}</h3>
                    <p className="text-sm text-muted-foreground">{brandDescription}</p>
                  </div>
                </div>
              </div>

              {/* Product Image */}
              <div className="relative aspect-[4/3] overflow-hidden group">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                  className="w-full h-full"
                >
                  <Image
                    src={productImage}
                    alt={productImageAlt}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  initial={{ x: "-100%" }}
                  animate={{ x: "200%" }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut"
                  }}
                />

                {/* Rating Badge */}
                <motion.div 
                  className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-elevation-2"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  style={{ transform: 'translateZ(30px)' }}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="Star" size={20} className="text-warning fill-current" />
                    <span className="text-lg font-bold text-foreground">{rating}</span>
                    <span className="text-sm text-muted-foreground">({reviewCount})</span>
                  </div>
                </motion.div>
              </div>

              {/* Product Details */}
              <div className="p-6" style={{ transform: 'translateZ(15px)' }}>
                <h4 className="text-2xl font-heading font-bold text-foreground mb-3">{productName}</h4>
                <p className="text-muted-foreground mb-4">{productDescription}</p>

                {/* Specs */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {productSpecs?.map((spec, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.05, x: 5 }}
                      className="flex items-center space-x-2 bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-border/50 shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
                    >
                      <Icon name={spec?.icon} size={18} className={accentColor} />
                      <div>
                        <p className="text-xs text-muted-foreground">{spec?.label}</p>
                        <p className="text-sm font-semibold text-foreground">{spec?.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <div>
                    <p className="text-sm text-muted-foreground">Vanaf</p>
                    <p className="text-3xl font-bold text-primary">{productPrice}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleAddToCompare}
                      className={`p-3 rounded-lg transition-colors ${
                        isAdded 
                          ? 'bg-success text-white' :'bg-gray-100 hover:bg-gray-200 text-foreground'
                      }`}
                      title="Toevoegen aan vergelijking"
                    >
                      <Icon name={isAdded ? 'Check' : 'GitCompare'} size={20} />
                    </motion.button>
                    <Link to="/quote-request-form">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button size="lg">
                          Offerte aanvragen
                          <Icon name="ArrowRight" size={20} className="ml-2" />
                        </Button>
                      </motion.div>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Reviews */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: 15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="space-y-4"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">Wat klanten zeggen</h3>
              <p className="text-muted-foreground mb-6">Echte ervaringen van tevreden klanten</p>
            </motion.div>

            {/* Reviews List */}
            <div className="space-y-4">
              {reviews?.map((review, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20, rotateX: -10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{ delay: 0.4 + index * 0.15, duration: 0.6 }}
                  whileHover={{ 
                    y: -8,
                    scale: 1.02,
                    rotateX: 3,
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  }}
                  className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15)] hover:shadow-[0_20px_40px_-5px_rgba(0,0,0,0.25)] transition-all perspective-1000"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* Rating Stars */}
                  <div className="flex items-center space-x-1 mb-3" style={{ transform: 'translateZ(10px)' }}>
                    {[...Array(5)]?.map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + index * 0.15 + i * 0.05 }}
                      >
                        <Icon 
                          name="Star" 
                          size={16} 
                          className={i < review?.rating ? "text-warning fill-current" : "text-gray-300"} 
                        />
                      </motion.div>
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-muted-foreground italic mb-4" style={{ transform: 'translateZ(8px)' }}>
                    "{review?.text}"
                  </p>

                  {/* Reviewer Info */}
                  <div className="flex items-center space-x-3" style={{ transform: 'translateZ(12px)' }}>
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-12 h-12 rounded-full overflow-hidden shadow-elevation-1"
                    >
                      <Image
                        src={review?.avatar}
                        alt={`${review?.name} avatar`}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                    <div>
                      <p className="font-semibold text-foreground">{review?.name}</p>
                      <p className="text-sm text-muted-foreground">{review?.location}</p>
                    </div>
                    <motion.div
                      className="ml-auto"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Icon name="CheckCircle" size={20} className="text-success" />
                    </motion.div>
                  </div>

                  {/* Additional Info */}
                  {review?.verified && (
                    <motion.div 
                      className="mt-3 pt-3 border-t border-border/50 flex items-center space-x-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 + index * 0.15 }}
                      style={{ transform: 'translateZ(5px)' }}
                    >
                      <Icon name="Shield" size={14} className="text-primary" />
                      <span className="text-xs text-muted-foreground">Geverifieerde aankoop</span>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* View All Reviews CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="text-center pt-4"
            >
              <Link to="/quote-request-form">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button variant="outline" size="lg">
                    Bekijk alle {reviewCount} reviews
                    <Icon name="ExternalLink" size={18} className="ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBrandSection;